import Delete from "@mui/icons-material/Delete";
import { Checkbox, IconButton, Stack, useTheme } from "@mui/material";
import { CheckboxField } from "components/Forms/CheckboxField";
import { TextInput } from "components/TextInput";
import React, { useCallback, useRef, useState } from "react";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { v4 } from "uuid";

import { TaskFormType } from "./types";

type SubtasksControlProps = {
  control: Control<TaskFormType>;
};

const SubtasksControl: React.FC<SubtasksControlProps> = ({ control }) => {
  const theme = useTheme();
  const {
    fields: subtasks,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "subtasks",
    keyName: "_id",
  });

  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const newSubtaskInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleAddSubTask = useCallback(() => {
    const title = newSubtaskTitle.trim();
    if (!title) return;
    append({ title, completed: false, id: v4() });
    setNewSubtaskTitle("");
    newSubtaskInputRef.current?.focus();
  }, [append, newSubtaskTitle]);

  const handleDeleteSubTask = useCallback(
    (id: string) => {
      const index = subtasks.findIndex((field) => field.id === id);
      if (index === -1) return;
      remove(index);
    },
    [subtasks, remove]
  );

  return (
    <Stack gap={theme.spacing(0.5)}>
      {subtasks.map((subtask, index) => (
        <Stack direction="row" key={subtask.id} alignItems="baseline">
          <CheckboxField
            name={`subtasks.${index}.completed`}
            control={control}
            label={""}
            fullWidth={false}
            formControlLabelProps={{ sx: { marginRight: 0 } }}
            noErrorDisplay
          />
          <Controller
            control={control}
            name={`subtasks.${index}.title`}
            render={({ field }) => (
              <Stack direction="column" gap={theme.spacing(0.5)} sx={{ flex: 1 }}>
                <TextInput
                  {...field}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      handleDeleteSubTask(subtask.id);
                      return;
                    }
                    field.onChange(e.target.value);
                  }}
                  variant="description"
                  multiline
                />
              </Stack>
            )}
          />
          <IconButton
            edge="end"
            onClick={() => handleDeleteSubTask(subtask.id)}
            sx={{ alignSelf: "flex-start" }}
          >
            <Delete />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" alignItems="center">
        <Checkbox disabled sx={{ paddingLeft: 0 }} />
        <TextInput
          placeholder="Add subtask..."
          fullWidth
          value={newSubtaskTitle}
          onChange={(e) => setNewSubtaskTitle(e.target.value)}
          onBlur={handleAddSubTask}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddSubTask();
            }
          }}
          inputRef={newSubtaskInputRef}
          variant="description"
        />
      </Stack>
    </Stack>
  );
};

export default SubtasksControl;
